import { useCallback, useEffect } from 'react';

import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';

import { api, baseUrl } from '@plugins/axios/api';

import { useAlert } from './useAlert';
import { useRecoil } from './useRecoil';

/**
 * api 요청과 응답의 처리 방법을 구현하는 hook
 *
 * 기타
 * - plugin:axios 활용 hook
 * - RootLayout 에서 1회만 선언하여 활용
 */
export const useRootAxiosInterceptor = () => {
  const { alertClick } = useAlert();
  const { useRecoilState, useAtom } = useRecoil();
  const [profile, setProfile] = useRecoilState(
    useAtom<{ uid: number; role: 'partner' | 'funeral'; name: string } | null>(
      'USER_PROFILE',
    ),
  );

  const getAccessToken = useCallback(
    async <T>(error: AxiosError<T>) => {
      // accessToken 유효기간 만료 시 재요청하는 함수
      const refreshToken = Cookies.get('REPLHADA_ADMIN_REFRESH_TOKEN');
      try {
        const {
          data: { accessToken },
        } = await axios.post(
          `${baseUrl}/auth/refresh`,
          {},
          {
            headers: { authorization: 'Bearer ' + refreshToken },
          },
        );

        Cookies.set('REPLHADA_ADMIN_ACCESS_TOKEN', accessToken);

        return api(error.config!);
      } catch (error: any) {
        if (
          error.response.data.code === 401 &&
          error.response.data.message === 'Invalid refresh token'
        ) {
          // accessToken 만료로 refreshToken을 사용해서 재요청했으나
          // refreshToken까지 만료되어 재로그인을 유도하는 로직
          alertClick('로그인 세션이 만료되었습니다.');
          Cookies.remove('REPLHADA_ADMIN_ACCESS_TOKEN');
          Cookies.remove('REPLHADA_ADMIN_REFRESH_TOKEN');
          setProfile(null);
          window.location.reload();
        }
      }
    },
    [alertClick, setProfile],
  );

  // 하위 컴포넌트에 설정 적용을 위한 useEffect
  useEffect(() => {
    const requestInterceptor = api.interceptors.request.use(
      (config: InternalAxiosRequestConfig<any>) => {
        // 요청을 보내기 전에 수행할 일
        const accesstoken = Cookies.get('REPLHADA_ADMIN_ACCESS_TOKEN');

        if (accesstoken) {
          config.headers!['authorization'] = 'Bearer ' + accesstoken;
        }

        return config;
      },
      (error: any) => {
        return Promise.reject(error);
      },
    );

    const responseInterceptor = api.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        // console.log('error', error.response);
        // 오류 응답을 처리
        const errorCode = error.response.data.code;

        if (errorCode === 401) {
          const errorMessage = error.response.data.message;
          if (errorMessage === 'Already login') {
            // alert('중복 로그인 되었습니다.');
            // Cookies.remove('REPLHADA_ADMIN_ACCESS_TOKEN');
            // Cookies.remove('REPLHADA_ADMIN_REFRESH_TOKEN');
            // window.location.reload();
          }

          if (errorMessage === 'Invalid access token') {
            return getAccessToken<AxiosError>(error);
          }
        }

        return Promise.reject(error);
      },
    );
  }, [getAccessToken]);
};
